import React, { useState, useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BeRealCameraCapture = ({ onCapture, timeLeft, onTimeout }) => {
  const [capturePhase, setCapturePhase] = useState('ready'); // 'ready', 'capturing', 'captured', 'uploading'
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [currentCamera, setCurrentCamera] = useState('environment');
  const [countdown, setCountdown] = useState(3);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(''); // 'processing', 'uploading', 'done'
  const [captureError, setCaptureError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const frontWebcamRef = useRef(null);
  const backWebcamRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    if (timeLeft <= 0 && capturePhase !== 'captured' && capturePhase !== 'uploading') {
      onTimeout?.();
    }
  }, [timeLeft, capturePhase, onTimeout]);

  useEffect(() => {
    return () => {
      if (countdownTimerRef?.current) clearInterval(countdownTimerRef?.current);
      if (progressTimerRef?.current) clearInterval(progressTimerRef?.current);
    };
  }, []);

  const simulateProgress = (from, to, duration) => {
    const steps = 20;
    const increment = (to - from) / steps;
    const interval = duration / steps;
    let current = from;
    progressTimerRef.current = setInterval(() => {
      current += increment;
      if (current >= to) {
        current = to;
        clearInterval(progressTimerRef?.current);
      }
      setUploadProgress(Math.round(current));
    }, interval);
  };

  const startCapture = () => {
    setCaptureError(null);
    setCapturePhase('capturing');
    setCountdown(3);

    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef?.current);
          captureSimultaneous();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const captureSimultaneous = () => {
    const frontImg = frontWebcamRef?.current?.getScreenshot();
    const backImg = backWebcamRef?.current?.getScreenshot();

    if (frontImg && backImg) {
      setFrontImage(frontImg);
      setBackImage(backImg);
      setCapturePhase('uploading');
      setUploadProgress(0);
      setUploadStage('processing');

      // Simulate processing → uploading → done
      simulateProgress(0, 40, 600);

      setTimeout(() => {
        setUploadStage('uploading');
        simulateProgress(40, 90, 1200);

        setTimeout(() => {
          setUploadProgress(100);
          setUploadStage('done');

          setTimeout(() => {
            setCapturePhase('captured');
            onCapture?.({ frontImage: frontImg, backImage: backImg });
          }, 500);
        }, 1300);
      }, 700);
    } else {
      setCapturePhase('ready');
      setCaptureError('No se pudieron capturar ambas cámaras. Asegúrate de que los permisos de cámara estén habilitados e intenta de nuevo.');
    }
  };

  const handleRetry = () => {
    setCaptureError(null);
    setRetryCount(prev => prev + 1);
    setFrontImage(null);
    setBackImage(null);
    setUploadProgress(0);
    setUploadStage('');
    setCapturePhase('ready');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs)?.padStart(2, '0')}`;
  };

  const stageLabel = {
    processing: 'Procesando capturas...',
    uploading: 'Subiendo imágenes...',
    done: '¡Listo!'
  };

  // Uploading phase — spinner overlay + progress bar
  if (capturePhase === 'uploading') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {[{ img: frontImage, label: 'Cámara Frontal', icon: 'User' }, { img: backImage, label: 'Cámara Trasera', icon: 'Camera' }]?.map(({ img, label, icon }) => (
            <div key={label} className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground text-center">{label}</p>
              <div className="relative rounded-xl overflow-hidden border-2 border-primary/30">
                <img src={img} alt={label} className="w-full h-auto opacity-50" />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
                  {uploadStage !== 'done' ? (
                    <div className="w-10 h-10 rounded-full border-4 border-white/30 border-t-white animate-spin" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-success flex items-center justify-center">
                      <Icon name="Check" size={20} className="text-white" />
                    </div>
                  )}
                </div>
                <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm">
                  <Icon name={icon} size={12} className="text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Progress bar */}
        <div className="p-4 bg-primary/10 rounded-xl border border-primary/30 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {uploadStage !== 'done' ? (
                <div className="w-4 h-4 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
              ) : (
                <Icon name="CheckCircle2" size={16} className="text-success" />
              )}
              <p className="text-sm font-semibold text-foreground">
                {stageLabel?.[uploadStage] || 'Procesando...'}
              </p>
            </div>
            <span className="text-sm font-bold text-primary">{uploadProgress}%</span>
          </div>
          <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-error rounded-full transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (capturePhase === 'captured') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground text-center">Cámara Frontal</p>
            <div className="relative rounded-xl overflow-hidden border-2 border-primary/30">
              <img src={frontImage} alt="Front camera capture" className="w-full h-auto" />
              <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm">
                <Icon name="User" size={12} className="text-white" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground text-center">Cámara Trasera</p>
            <div className="relative rounded-xl overflow-hidden border-2 border-success/30">
              <img src={backImage} alt="Back camera capture" className="w-full h-auto" />
              <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm">
                <Icon name="Camera" size={12} className="text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 bg-success/10 rounded-xl border border-success/30">
          <div className="flex items-center space-x-3">
            <Icon name="CheckCircle2" size={24} className="text-success" />
            <div>
              <p className="text-sm font-semibold text-foreground">¡Captura Exitosa!</p>
              <p className="text-xs text-muted-foreground">Ambas cámaras capturadas simultáneamente</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (capturePhase === 'capturing') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
            <Webcam
              ref={frontWebcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode: 'user' }}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm">
              <p className="text-xs text-white font-semibold">Frontal</p>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
            <Webcam
              ref={backWebcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode: 'environment' }}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm">
              <p className="text-xs text-white font-semibold">Trasera</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-error flex items-center justify-center animate-pulse shadow-2xl">
            <span className="text-6xl font-bold text-white">{countdown}</span>
          </div>
        </div>

        <div className="p-4 bg-primary/10 rounded-xl border border-primary/30">
          <div className="flex items-center space-x-3">
            <Icon name="Camera" size={20} className="text-primary animate-pulse" />
            <p className="text-sm text-foreground font-semibold">Capturando en {countdown}...</p>
          </div>
        </div>
      </div>
    );
  }

  // Ready state
  return (
    <div className="space-y-4">
      <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
        <Webcam
          ref={currentCamera === 'user' ? frontWebcamRef : backWebcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: currentCamera }}
          className="w-full h-full object-cover"
        />

        <div className="absolute -left-full top-0 w-0 h-0 overflow-hidden opacity-0">
          <Webcam
            ref={currentCamera === 'user' ? backWebcamRef : frontWebcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: currentCamera === 'user' ? 'environment' : 'user' }}
          />
        </div>

        <button
          onClick={() => setCurrentCamera(prev => prev === 'user' ? 'environment' : 'user')}
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <Icon name="RefreshCw" size={20} className="text-white" />
        </button>

        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full bg-black/70 backdrop-blur-sm">
          <p className="text-white font-bold text-lg">{formatTime(timeLeft)}</p>
        </div>

        <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm">
          <div className="flex items-center space-x-2">
            <Icon name={currentCamera === 'user' ? 'User' : 'Camera'} size={14} className="text-white" />
            <p className="text-xs text-white font-semibold">
              {currentCamera === 'user' ? 'Frontal' : 'Trasera'}
            </p>
          </div>
        </div>
      </div>

      {/* Error recovery */}
      {captureError && (
        <div className="p-3 bg-error/10 border border-error/30 rounded-xl">
          <div className="flex items-start justify-between space-x-2">
            <div className="flex items-start space-x-2 flex-1">
              <Icon name="AlertCircle" size={16} className="text-error mt-0.5 shrink-0" />
              <p className="text-xs text-error">{captureError}</p>
            </div>
            <button
              onClick={handleRetry}
              className="flex items-center space-x-1 text-xs font-semibold text-error hover:text-error/80 transition-colors shrink-0 ml-2"
            >
              <Icon name="RefreshCw" size={13} />
              <span>Reintentar{retryCount > 0 ? ` (${retryCount})` : ''}</span>
            </button>
          </div>
        </div>
      )}

      <div className="p-4 bg-warning/10 rounded-xl border border-warning/30">
        <div className="flex items-start space-x-3">
          <Icon name="Zap" size={20} className="text-warning mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="font-semibold mb-1">Modo BeReal - Captura Simultánea:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Ambas cámaras se capturarán al mismo tiempo</li>
              <li>Cuenta regresiva de 3 segundos antes de capturar</li>
              <li>Solo tienes UNA oportunidad - sin repeticiones</li>
              <li>Prepárate antes de presionar el botón</li>
            </ul>
          </div>
        </div>
      </div>

      <Button
        onClick={startCapture}
        size="lg"
        fullWidth
        className="bg-gradient-to-r from-primary to-error hover:from-primary/90 hover:to-error/90"
      >
        <Icon name="Zap" size={20} className="mr-2" />
        Iniciar Captura BeReal
      </Button>
    </div>
  );
};

export default BeRealCameraCapture;